'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function LoanCalculator() {
  const t = useTranslations("tools.loanCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    loanAmount: 100000,
    loanYears: 10,
    loanMonths: 0,
    interestRate: 6,
    compoundFrequency: 12,
    paymentFrequency: 12,
  });

  const [results, setResults] = useState<any>(null);
  const [showAmortization, setShowAmortization] = useState(false);
  const [schedule, setSchedule] = useState<any[]>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const calculateLoan = (principal: number, annualRate: number, totalMonths: number, compoundFreq: number, paymentFreq: number) => {
    const periodicRate = (annualRate / 100) / compoundFreq;
    const paymentsPerYear = paymentFreq;
    const effectiveRate = Math.pow(1 + periodicRate, compoundFreq / paymentsPerYear) - 1;

    const totalPayments = Math.ceil(totalMonths / (12 / paymentsPerYear));

    const payment = principal * (effectiveRate * Math.pow(1 + effectiveRate, totalPayments)) /
                   (Math.pow(1 + effectiveRate, totalPayments) - 1);

    return {
      payment: payment,
      totalPayments: totalPayments,
      effectiveRate: effectiveRate
    };
  };

  const generateAmortizationSchedule = (principal: number, payment: number, rate: number, totalPayments: number) => {
    const scheduleData = [];
    let balance = principal;

    for (let i = 1; i <= totalPayments; i++) {
      const interestPayment = balance * rate;
      const principalPayment = payment - interestPayment;
      balance -= principalPayment;

      if (balance < 0) balance = 0;

      scheduleData.push({
        paymentNum: i,
        payment: payment,
        principal: principalPayment,
        interest: interestPayment,
        balance: balance
      });
    }

    return scheduleData;
  };

  const formatCurrency = (amount: number) => {
    return '$' + amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const totalMonths = (inputs.loanYears * 12) + inputs.loanMonths;

    if (totalMonths === 0) {
      alert(t('validTermError'));
      return;
    }

    const result = calculateLoan(
      inputs.loanAmount,
      inputs.interestRate,
      totalMonths,
      inputs.compoundFrequency,
      inputs.paymentFrequency
    );

    const scheduleData = generateAmortizationSchedule(
      inputs.loanAmount,
      result.payment,
      result.effectiveRate,
      result.totalPayments
    );

    const totalPaid = result.payment * result.totalPayments;
    const totalInterest = totalPaid - inputs.loanAmount;

    setResults({
      monthlyPayment: result.payment,
      totalPayments: totalPaid,
      totalInterest: totalInterest,
      numPayments: result.totalPayments,
      principal: inputs.loanAmount,
    });

    setSchedule(scheduleData);
    setShowAmortization(false);
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M", x, y,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1100px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1rem' },
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = {
    display: 'block',
    fontWeight: 500,
    color: '#334155',
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem' },
    { padding: '0.875rem' },
    { padding: '1rem' }
  );

  const inputGroupStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      fontWeight: 700,
      color: 'white',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultGridStyle = {
    display: 'grid',
    gap: '1rem',
    marginBottom: '2rem',
  };

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #2563eb',
    },
    { padding: '0.875rem', flexDirection: 'column' as const, gap: '0.5rem' },
    { padding: '1rem', flexDirection: 'row' as const },
    { padding: '1rem', flexDirection: 'row' as const }
  );

  const resultItemHighlightStyle = {
    ...resultItemStyle,
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    borderLeftColor: '#1d4ed8',
  };

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 500, color: '#475569' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const resultValueLargeStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.75rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '1.5rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2.5rem', marginBottom: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const toggleButtonStyle = getResponsiveStyle(
    {
      background: '#f1f5f9',
      color: '#2563eb',
      border: '2px solid #2563eb',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    { padding: '0.75rem 1.5rem', fontSize: '0.95rem' },
    { padding: '0.875rem 1.75rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1rem' }
  );

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.9rem',
    overflowX: 'auto' as const,
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
            <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={calculatorCardStyle}>
              <div style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📊 {t('loanDetailsTitle')}
              </div>

              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="loan-amount">{t('loanAmountLabel')}</label>
                  <div style={{ position: 'relative' as const }}>
                    <input
                      type="number"
                      id="loan-amount"
                      value={inputs.loanAmount}
                      onChange={(e) => setInputs({...inputs, loanAmount: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="1000"
                      required
                      style={inputStyle}
                    />
                    <span style={{ position: 'absolute' as const, right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '0.9rem', pointerEvents: 'none' as const }}>$</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('loanTermLabel')}</label>
                  <div style={inputGroupStyle}>
                    <div style={{ position: 'relative' as const }}>
                      <input
                        type="number"
                        value={inputs.loanYears}
                        onChange={(e) => setInputs({...inputs, loanYears: parseInt(e.target.value) || 0})}
                        min="0"
                        max="50"
                        style={inputStyle}
                      />
                      <span style={{ position: 'absolute' as const, right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '0.9rem', pointerEvents: 'none' as const }}>{t('yearsLabel')}</span>
                    </div>
                    <div style={{ position: 'relative' as const }}>
                      <input
                        type="number"
                        value={inputs.loanMonths}
                        onChange={(e) => setInputs({...inputs, loanMonths: parseInt(e.target.value) || 0})}
                        min="0"
                        max="11"
                        style={inputStyle}
                      />
                      <span style={{ position: 'absolute' as const, right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '0.9rem', pointerEvents: 'none' as const }}>{t('monthsLabel')}</span>
                    </div>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest-rate">{t('interestRateLabel')}</label>
                  <div style={{ position: 'relative' as const }}>
                    <input
                      type="number"
                      id="interest-rate"
                      value={inputs.interestRate}
                      onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="100"
                      step="0.01"
                      required
                      style={inputStyle}
                    />
                    <span style={{ position: 'absolute' as const, right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '0.9rem', pointerEvents: 'none' as const }}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="compound-frequency">{t('compoundFrequencyLabel')}</label>
                  <select
                    id="compound-frequency"
                    value={inputs.compoundFrequency}
                    onChange={(e) => setInputs({...inputs, compoundFrequency: parseInt(e.target.value)})}
                    style={inputStyle}
                  >
                    <option value="12">{t('monthlyAPR')}</option>
                    <option value="1">{t('annuallyAPY')}</option>
                    <option value="2">{t('semiAnnually')}</option>
                    <option value="4">{t('quarterly')}</option>
                    <option value="24">{t('semiMonthly')}</option>
                    <option value="26">{t('biweekly')}</option>
                    <option value="52">{t('weekly')}</option>
                    <option value="365">{t('daily')}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="payment-frequency">{t('paymentFrequencyLabel')}</label>
                  <select
                    id="payment-frequency"
                    value={inputs.paymentFrequency}
                    onChange={(e) => setInputs({...inputs, paymentFrequency: parseInt(e.target.value)})}
                    style={inputStyle}
                  >
                    <option value="12">{t('everyMonth')}</option>
                    <option value="1">{t('everyYear')}</option>
                    <option value="4">{t('everyQuarter')}</option>
                    <option value="2">{t('every6Months')}</option>
                    <option value="26">{t('every2Weeks')}</option>
                    <option value="52">{t('everyWeek')}</option>
                  </select>
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            <div style={calculatorCardStyle}>
              <div style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                💡 {t('quickTipsTitle')}
              </div>
              <div style={{ color: '#475569', lineHeight: 1.8 }}>
                <p style={{ marginBottom: '1rem' }}><strong>{t('tip1Title')}</strong> {t('tip1Text')}</p>
                <p style={{ marginBottom: '1rem' }}><strong>{t('tip2Title')}</strong> {t('tip2Text')}</p>
                <p style={{ marginBottom: '1rem' }}><strong>{t('tip3Title')}</strong> {t('tip3Text')}</p>
                <p><strong>{t('tip4Title')}</strong> {t('tip4Text')}</p>
              </div>
            </div>
          </div>

          {results && (
            <div style={resultsStyle}>
              <h3 style={{ fontSize: '1.5rem', color: colors.textPrimary, marginBottom: '1.5rem', fontWeight: 600 }}>
                📈 {t('resultsTitle')}
              </h3>

              <div style={resultGridStyle}>
                <div style={resultItemHighlightStyle}>
                  <span style={resultLabelStyle}>{t('monthlyPaymentLabel')}</span>
                  <span style={resultValueLargeStyle}>{formatCurrency(results.monthlyPayment)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('totalPaymentsLabel')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalPayments)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('totalInterestLabel')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalInterest)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('numPaymentsLabel')}</span>
                  <span style={resultValueStyle}>{results.numPayments}</span>
                </div>
              </div>

              <div style={{ margin: '2rem 0', padding: '1.5rem', background: '#f8fafc', borderRadius: '0.75rem' }}>
                <h4 style={{ textAlign: 'center' as const, color: colors.textPrimary, marginBottom: '1rem' }}>{t('paymentBreakdownTitle')}</h4>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '1.5rem 0' }}>
                  <svg width="200" height="200">
                    {(() => {
                      const total = results.principal + results.totalInterest;
                      const principalPercent = (results.principal / total) * 100;
                      const principalAngle = (principalPercent / 100) * 360;
                      const radius = 80;
                      const cx = 100;
                      const cy = 100;

                      const principalPath = describeArc(cx, cy, radius, 0, principalAngle);
                      const interestPath = describeArc(cx, cy, radius, principalAngle, 360);

                      return (
                        <>
                          <path d={principalPath} fill="#2563eb" stroke="white" strokeWidth="2"/>
                          <path d={interestPath} fill="#f59e0b" stroke="white" strokeWidth="2"/>
                          <circle cx={cx} cy={cy} r="40" fill="white"/>
                          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize="24" fontWeight="bold" fill={colors.textPrimary}>
                            {principalPercent.toFixed(0)}%
                          </text>
                          <text x={cx} y={cy + 20} textAnchor="middle" fontSize="12" fill="#64748b">
                            {t('principalLabel')}
                          </text>
                        </>
                      );
                    })()}
                  </svg>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' as const }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '1rem', height: '1rem', borderRadius: '0.25rem', background: '#2563eb' }}></div>
                    <span>{t('principalLabel')}: <strong>{((results.principal / (results.principal + results.totalInterest)) * 100).toFixed(1)}%</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '1rem', height: '1rem', borderRadius: '0.25rem', background: '#f59e0b' }}></div>
                    <span>{t('interestLabel')}: <strong>{((results.totalInterest / (results.principal + results.totalInterest)) * 100).toFixed(1)}%</strong></span>
                  </div>
                </div>
              </div>

              <div style={{ textAlign: 'center' as const, marginTop: '1.5rem' }}>
                <button
                  onClick={() => setShowAmortization(!showAmortization)}
                  style={toggleButtonStyle}
                >
                  {showAmortization ? t('hideAmortizationButton') : t('viewAmortizationButton')}
                </button>
              </div>

              {showAmortization && (
                <div style={{ marginTop: '1.5rem', overflowX: 'auto' as const }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={{ background: '#1e293b', color: 'white', padding: '0.75rem', textAlign: 'left' as const, fontWeight: 600 }}>{t('paymentNumHeader')}</th>
                        <th style={{ background: '#1e293b', color: 'white', padding: '0.75rem', textAlign: 'left' as const, fontWeight: 600 }}>{t('paymentAmountHeader')}</th>
                        <th style={{ background: '#1e293b', color: 'white', padding: '0.75rem', textAlign: 'left' as const, fontWeight: 600 }}>{t('principalHeader')}</th>
                        <th style={{ background: '#1e293b', color: 'white', padding: '0.75rem', textAlign: 'left' as const, fontWeight: 600 }}>{t('interestHeader')}</th>
                        <th style={{ background: '#1e293b', color: 'white', padding: '0.75rem', textAlign: 'left' as const, fontWeight: 600 }}>{t('balanceHeader')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.map((row, index) => (
                        <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                          <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>{row.paymentNum}</td>
                          <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>{formatCurrency(row.payment)}</td>
                          <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>{formatCurrency(row.principal)}</td>
                          <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>{formatCurrency(row.interest)}</td>
                          <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>{formatCurrency(row.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
          <li style={listItemStyle}>{t('howToStep6')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insight1')}</p>
        <p style={paragraphStyle}>{t('insight2')}</p>
        <p style={paragraphStyle}>{t('insight3')}</p>
        <p style={paragraphStyle}>{t('insight4')}</p>
        <p style={paragraphStyle}>{t('insight5')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('aprVsInterestTitle')}</h3>
        <p style={paragraphStyle}>{t('aprVsInterestText')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('extraPaymentsTitle')}</h3>
        <p style={paragraphStyle}>{t('extraPaymentsText')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('loanTypesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('mortgageTitle')}</strong> {t('mortgageText')}</li>
          <li style={listItemStyle}><strong>{t('autoLoanTitle')}</strong> {t('autoLoanText')}</li>
          <li style={listItemStyle}><strong>{t('personalLoanTitle')}</strong> {t('personalLoanText')}</li>
          <li style={listItemStyle}><strong>{t('studentLoanTitle')}</strong> {t('studentLoanText')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.bankrate.com/loans/loan-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Bankrate - Loan Calculator
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.nerdwallet.com/personal-loans/calculators/loan-payment" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              NerdWallet - Personal Loan Calculator
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/payment-calculator.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Calculator.net - Payment Calculator
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.mortgagecalculator.org" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Mortgage Calculator
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://sf.freddiemac.com/tools-learning/technology-tools/our-solutions/income-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Freddie Mac - Income Calculator
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}